import { useDispatch, useSelector } from 'react-redux'
import { Button } from '@material-ui/core'
import styles from './VNPriceIndicator.module.scss'
import { setConnectionRequiredDialog, setPriceAdjustDialog } from '../../VNDialogs/ActionCreators'
import { useMemo } from 'react'
import { getNetworkAvailableValue } from '../../utils/networkConnected'
import { APPLICATION_STRINGS } from '../../strings'
import { makeGetSelectedOrder } from '../../selectors/order'
import { ReactComponent as ErrorIcon } from '../../assets/icons/error.svg'
import { getMobilePaySetting } from '../../selectors/revenueCenter'
import { MOBILE_PAY_SHOW_QR } from '../../VNTenders/Utils'

/**
 * Price indicator component with price adjustment support
 *
 * @param {Object} props
 * @param {number} props.defaultPriceInCents
 * @param {number} [props.priceAdjustmentInCents=null]
 * @param {boolean} [props.fromViewItemDetail=false]
 * @returns {React.Element}
 */
const VNPriceIndicator = ({ defaultPriceInCents, priceAdjustmentInCents = null, fromViewItemDetail = false }) => {
  const dispatch = useDispatch()
  const networkConnected = getNetworkAvailableValue()
  const getSelectedOrder = makeGetSelectedOrder()
  const order = useSelector(getSelectedOrder)
  const mobilePaySetting = useSelector(getMobilePaySetting)

  //open items from orders already created will be treated as regular items.
  const isOpenPrice = defaultPriceInCents === null && !fromViewItemDetail
  const price = `$${(defaultPriceInCents / 100).toFixed(2)}`

  const isPristine = useMemo(() => {
    if (priceAdjustmentInCents === null) {
      return true
    }

    if (defaultPriceInCents === undefined) {
      return true
    }

    return priceAdjustmentInCents === defaultPriceInCents
  }, [priceAdjustmentInCents, defaultPriceInCents])

  const onClickHandler = () => {
    if (!networkConnected && order?.wasCreatedInStadium) {
      dispatch(
        setConnectionRequiredDialog({ open: true, subtext: APPLICATION_STRINGS.PRICE_ADJUST_NOT_ALLOWED_OFFLINE_SYNCED_ORDER }),
      )
      return
    }
    dispatch(setPriceAdjustDialog(true, priceAdjustmentInCents ?? defaultPriceInCents, priceAdjustmentInCents))
  }

  if (isOpenPrice) {
    return null
  }

  const Disclaimer = () => {
    if (mobilePaySetting !== MOBILE_PAY_SHOW_QR) {
      return null
    }

    return (
      <div className={styles.disclaimerContainer}>
        <div className={styles.errorIconContainer}>
          <ErrorIcon className={styles.errorIcon} />
        </div>
        <div className={styles.disclaimer}>
          Orders containing price adjusted items cannot be paid by scanning from the Mobile App.
        </div>
      </div>
    )
  }

  if (isPristine) {
    return (
      <>
        <div className={styles.container}>
          <span className={styles.price}>{price}</span>
          <Button className={styles.button} onClick={onClickHandler}>
            Adjust
          </Button>
        </div>
        <Disclaimer />
      </>
    )
  }

  return (
    <>
      <div className={styles.container}>
        <span className={styles.price}>{`$${(priceAdjustmentInCents / 100).toFixed(2)}`}</span>
        <span className={styles.original}>{price}</span>
        <Button className={styles.button} onClick={onClickHandler}>
          Adjust
        </Button>
      </div>
      <Disclaimer />
    </>
  )
}

export default VNPriceIndicator
